import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Alert,
  Grid,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Lock as LockIcon,
  Slideshow as SlideshowIcon,
  Description as DescriptionIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';

const INVESTOR_PASSWORD = 'huslrz2026';

const InvestorMaterialsPage: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === INVESTOR_PASSWORD) {
      setIsAuthenticated(true);
      setError('');
      // Set both session keys for access to all protected content
      sessionStorage.setItem('investor_auth', 'true');
      sessionStorage.setItem('huslrz_pitch_auth', 'true');
    } else {
      setError('Incorrect password. Please try again.');
    }
  };

  React.useEffect(() => {
    const auth = sessionStorage.getItem('investor_auth');
    if (auth === 'true') {
      setIsAuthenticated(true);
      // Ensure pitch auth is also set for protected content access
      sessionStorage.setItem('huslrz_pitch_auth', 'true');
    }
  }, []);

  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #0D3D3A 0%, #14B8A6 100%)',
          px: { xs: 2, sm: 3 },
          py: { xs: 4, sm: 0 },
        }}
      >
        <Helmet>
          <title>Investor Materials — HUSLRZ</title>
        </Helmet>
        <Card sx={{ maxWidth: 420, width: '100%' }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <LockIcon sx={{ fontSize: { xs: 40, sm: 48 }, color: '#14B8A6', mb: 1.5 }} />
              <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                HUSLRZ Investor Materials
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                This content is password protected. Please enter the access code to continue.
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handlePasswordSubmit}>
              <TextField
                fullWidth
                label="Access Code"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  ),
                }}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                sx={{
                  backgroundColor: '#14B8A6',
                  '&:hover': { backgroundColor: '#0D9488' },
                  py: 1.5,
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                }}
              >
                Access Materials
              </Button>
            </form>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', py: { xs: 2, sm: 4 } }}>
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        <Box sx={{ mb: { xs: 2, sm: 4 } }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            HUSLRZ Investor Materials
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Confidential investor documents for HUSLRZ Seed Round
          </Typography>
        </Box>

        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ mb: { xs: 2, sm: 3 } }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Pitch Deck" icon={<SlideshowIcon />} iconPosition="start" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }} />
          <Tab label="Pitch Deck V2" icon={<SlideshowIcon />} iconPosition="start" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }} />
          <Tab label="Pitch Script" icon={<DescriptionIcon />} iconPosition="start" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }} />
          <Tab label="CEO Strategy" icon={<DescriptionIcon />} iconPosition="start" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }} />
          <Tab label="Survey Reports" icon={<DescriptionIcon />} iconPosition="start" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }} />
        </Tabs>

        {activeTab === 0 && (
          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1, mb: 2 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Simplified Pitch Deck
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => window.open('/investor-pitch/', '_blank')}
                  size="small"
                >
                  Open in New Tab
                </Button>
              </Box>
              <Box
                sx={{
                  width: '100%',
                  height: { xs: '50vh', sm: '70vh' },
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <iframe
                  src="/investor-pitch/"
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title="HUSLRZ Simplified Investor Pitch"
                />
              </Box>
            </CardContent>
          </Card>
        )}

        {activeTab === 1 && (
          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1, mb: 2 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Idea Presentation
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => window.open('/idea-pitch/', '_blank')}
                  size="small"
                >
                  Open in New Tab
                </Button>
              </Box>
              <Box
                sx={{
                  width: '100%',
                  height: { xs: '50vh', sm: '70vh' },
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <iframe
                  src="/idea-pitch/"
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title="HUSLRZ Idea Presentation"
                />
              </Box>
            </CardContent>
          </Card>
        )}

        {activeTab === 2 && (
          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1, mb: 2 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Pitch Script & Speaker Notes
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => window.open('/pitch-script/', '_blank')}
                  size="small"
                >
                  Open in New Tab
                </Button>
              </Box>
              <Box
                sx={{
                  width: '100%',
                  height: { xs: '50vh', sm: '70vh' },
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <iframe
                  src="/pitch-script/"
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title="HUSLRZ Pitch Script"
                />
              </Box>
            </CardContent>
          </Card>
        )}

        {activeTab === 3 && (
          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1, mb: 2 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  CEO Strategy Bible
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => window.open('/ceo-strategy/', '_blank')}
                  size="small"
                >
                  Open in New Tab
                </Button>
              </Box>
              <Box
                sx={{
                  width: '100%',
                  height: { xs: '50vh', sm: '70vh' },
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <iframe
                  src="/ceo-strategy/"
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title="HUSLRZ CEO Strategy"
                />
              </Box>
            </CardContent>
          </Card>
        )}

        {activeTab === 4 && (
          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1, mb: 2 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Market Research & Survey Reports
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => window.open('/survey-reports/', '_blank')}
                  size="small"
                >
                  Open in New Tab
                </Button>
              </Box>
              <Box
                sx={{
                  width: '100%',
                  height: { xs: '50vh', sm: '70vh' },
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <iframe
                  src="/survey-reports/"
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title="HUSLRZ Survey Reports"
                />
              </Box>
            </CardContent>
          </Card>
        )}


        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button
            variant="text"
            color="error"
            onClick={() => {
              sessionStorage.removeItem('investor_auth');
              sessionStorage.removeItem('huslrz_pitch_auth');
              setIsAuthenticated(false);
              setPassword('');
            }}
          >
            Lock Materials
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default InvestorMaterialsPage;
